import React, { useEffect, useRef, useState, useCallback } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { ASTRA_PATH } from '../../utils/astraLink';
import {
    XMarkIcon, ChevronDownIcon, PlusIcon,
    PencilIcon, TrashIcon, CheckIcon, PaperClipIcon,
} from '@heroicons/react/24/outline';
import {
    conciergeChat, conciergeChatStream, listConciergeThreads, createConciergeThread,
    renameConciergeThread, deleteConciergeThread, getConciergeThreadMessages,
    confirmConciergeAction, declineConciergeAction, uploadStudentProfile, getAstraPopular,
} from '../../services/api';
import PopularShelf from './PopularShelf';
import usePopularQuestions from '../../hooks/usePopularQuestions';
import { studentShelf } from '../../content/astraPopular';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import { notifyProgress } from '../../services/progressEvent';
import ReplyFeedback from './ReplyFeedback';
import { track, EVENTS } from '../../services/analytics';
import { REPLAY_PRIVATE_CLASS } from '../../services/analyticsPrivacy';
import AstraAvatar from './AstraAvatar';
import MajorPicker from './MajorPicker';
import MajorChoicePicker from './MajorChoicePicker';
import SchoolPicker from './SchoolPicker';
import ListPanel from './ListPanel';
import EssayPromptPicker from './EssayPromptPicker';
import AddStatusLine from './AddStatusLine';
import CarryBanner from './CarryBanner';
import ReadCard from '../landing/astraLite/ReadCard';
import { activityLine } from './activityLabels';
import {
    CARRY_NOTICE_EVENT, claimCarryOpen, dismissNotice, readNotice, runCarryImportWithin, setCarryArchived, writeNotice,
} from '../../services/astraCarry';

const KICKOFF = '(the student just opened the chat for the first time — greet them warmly in one short line and ask the first question)';
const FOCUSABLE_SELECTOR = 'button, [href], input, textarea, select, [tabindex]:not([tabindex="-1"])';
const DEFAULT_TITLE = 'New conversation';
// The founder-facing copy call on filing (#1617): "Saved under" is the truth
// — it is where the conversation LIVES. Anything like "Astra will focus on X"
// would promise a restriction the harness does not enforce. Exported because
// /astra prints the same sentence beside its title; two copies of a promise
// is how one of them ends up overstating what she does.
// The Patent Lab tools that WRITE the invention record (concierge_tools
// BUILDER_TOOLS minus the reads; pinned by BuilderWriteTools.test.js). A turn
// that ran one changed the page behind the chat, so the page must re-read.
// The capture-mode ones (builder_answer, start_builder_interview) run in the
// turn itself with no card, which is why the confirm path alone missed them.
export const BUILDER_WRITE_TOOLS = Object.freeze([
    'start_builder_interview', 'builder_answer', 'add_to_builder_answer', 'rewrite_builder_answer',
    'request_builder_draft', 'add_prior_art', 'request_prior_art_search', 'review_prior_art_match',
    'request_specification', 'record_filing', 'add_filing_to_application',
]);

export const SCOPE_BAND_LINE = 'Saved under this school. Astra can pull your fit, roadmap and essays for it.';

// The carried thread's dividers (#2945): before the landing questions, and
// where signed-in Astra takes over.
function CarryDivider({ label, testid }) {
    return (
        <div data-testid={testid} className="flex items-center gap-3 py-1 text-[11px] font-medium text-[#6B6B6B]">
            <span className="h-px flex-1 bg-[#E0DED8]" aria-hidden="true" />
            {label}
            <span className="h-px flex-1 bg-[#E0DED8]" aria-hidden="true" />
        </div>
    );
}

export function carryDividerLabel(at) {
    let day = '';
    try {
        if (at) day = new Date(at).toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
    } catch { /* no date: the words alone */ }
    return day && day !== 'Invalid Date' ? `Before you signed in · ${day}` : 'Before you signed in';
}

const toDisplayMessages = (serverMessages) =>
    (serverMessages || []).map((m) => ({ role: m.role, text: m.text }));

const toGeminiHistory = (serverMessages) =>
    (serverMessages || []).map((m) => ({
        role: m.role === 'student' ? 'user' : 'model',
        parts: [{ text: m.text }],
    }));

/**
 * The Concierge conversation panel. Design intent ("world class" per the
 * CEO's brief): an Intercom-shaped anchored panel, not a takeover — the
 * student can still see and reach the page behind it. One question at a
 * time from the backend's contract; a save chip appears the instant a field
 * is captured (immediate, legible feedback that they're being heard, not
 * harvested); a highlight card appears once the profile is read-ready.
 *
 * Named, switchable conversation threads (founder 2026-08-02: "we should
 * allow the user to store different conversation threads and rename them...
 * pick up whatever thread he wants or start a new one"). Preferences/memory
 * are cross-thread on the SERVER — nothing thread-specific is duplicated
 * here beyond the transcript itself, so switching threads never loses
 * context about who the student is.
 *
 * Stays mounted for its whole lifetime once first opened (the parent
 * launcher controls this via `visible`, not conditional mounting) — closing
 * must never discard the conversation or re-fire a kickoff turn on reopen.
 *
 * TWO LAYOUTS, ONE CHAT (#2210, founder pick A from the mocks):
 *   'drawer' — the anchored panel described above. The default, unchanged.
 *   'page'   — the same conversation filling /astra's column. No fixed
 *              positioning, no dialog role or focus trap (it is the page, not
 *              a layer over one), no close button, and no header thread
 *              switcher: the page owns a rail that lists the conversations.
 *
 * Everything else — the turn loop, the caps, the confirm cards, the pickers,
 * the honesty guards — is shared, because a second implementation of a
 * surface students trust is how drift ships. This component stays the OWNER
 * of thread mutations; the page drives them through `chatApi` and hears about
 * the results through `onThreadsChanged` / `onActiveThreadChanged`, so there
 * is exactly one copy of "which conversation is open".
 */
export default function ConciergeChat({ visible, onClose, onDismissForSession, onProfileMaybeChanged, email,
                                       initialAsk = null, onAskConsumed = null,
                                       initialScope = null, initialThreadId = null,
                                       initialTopic = null,
                                       onThreadConsumed = null, startBlank = false, leadIn = null,
                                       layout = 'drawer', chatApi = null,
                                       onThreadsChanged = null, onActiveThreadChanged = null }) {
    const isPage = layout === 'page';
    const navigate = useNavigate();
    // On the Astra page the conversation IS the destination (founder
    // 2026-09-15: a COSMOS answer yanked the student to the summer programs
    // page mid-conversation; "now that we have a dedicated page for Astra,
    // we should not navigate away from it during a conversation"). The
    // drawer keeps following the answer; the page offers the answer's
    // screen as the view link on the message, and the upload's profile
    // landing as the saved chip, so every door stays a click away.
    const onAstraPage = useLocation().pathname === ASTRA_PATH;
    // A plain <a href> forces a full browser navigation — which remounts the
    // whole app and destroys this panel's live thread (founder 2026-08-03:
    // clicking a suggestion chip made the concierge "forget" the
    // conversation entirely). A left-click routes client-side instead;
    // modifier-clicks / middle-clicks fall through to the real <a> so
    // "open in new tab" still works.
    // Already on that screen? Then navigating is a no-op that still clears
    // query params and remounts route children for nothing.
    const isHere = useCallback((href) => {
        try {
            const url = new URL(href, window.location.origin);
            return url.pathname === window.location.pathname
                && url.search === window.location.search;
        } catch { return false; }
    }, []);
    const goto = useCallback((href) => (e) => {
        if (e.defaultPrevented || e.button !== 0 || e.metaKey || e.ctrlKey
            || e.shiftKey || e.altKey) return;
        e.preventDefault();
        navigate(href);
    }, [navigate]);
    const [messages, setMessages] = useState([]);   // {role:'concierge'|'student', text, savedFields?}
    const [draft, setDraft] = useState('');
    const [sending, setSending] = useState(false);
    // True while that blank conversation is open and settled: a question
    // handed in then (the opener's chips) is sent at once, with no thread id,
    // instead of waiting for a greeting that never comes (review #3786).
    const [blankOpen, setBlankOpen] = useState(false);
    const [ready, setReady] = useState(false);
    const [celebrate, setCelebrate] = useState(null);
    const [celebrateCta, setCelebrateCta] = useState(null);   // server-chosen, per-student
    // Every primary option for the ready moment (usually odds + majors),
    // each with its own `why` — a real choice, not one silent pick
    // (founder 2026-08-03). Falls back to [celebrateCta] when absent so
    // older/degraded responses still render one button.
    const [celebrateChoices, setCelebrateChoices] = useState(null);
    const [error, setError] = useState(null);
    const [limited, setLimited] = useState(false);
    const [suggestions, setSuggestions] = useState([]);   // server-attached next-step chips
    // "Students are asking" (#5258): read once per account while
    // astra_popular_questions is on for it; null draws nothing.
    const popularFlag = usePopularQuestions();
    const [popular, setPopular] = useState(null);
    useEffect(() => {
        if (!popularFlag.on || !email) return undefined;
        let alive = true;
        Promise.resolve(getAstraPopular(email)).then((r) => {
            if (alive && r?.success && r.popular) setPopular(r.popular);
        }).catch(() => {});
        return () => { alive = false; };
    }, [popularFlag.on, email]);
    // Flow progress (#1177/#1180): {id, step, total} while a task flow owns
    // the conversation, null the moment it doesn't. The student should be
    // able to see they're on a short, finite path — a mid-flow turn shows no
    // standing chips, so without this the panel gives no sign the questions
    // are going somewhere. Only the id is on the wire (no title), so the pill
    // says exactly what the payload knows: the count.
    const [flow, setFlow] = useState(null);
    // Agentic (ADR 0004): the pending-action confirmation card and the
    // in-flight tool-activity labels. Both are ephemeral turn state — the
    // transcript itself stays text-only.
    const [pendingAction, setPendingAction] = useState(null);   // {nonce, title, subtitle}
    const [actionBusy, setActionBusy] = useState(false);

    const [threads, setThreads] = useState([]);
    const [activeThreadTitle, setActiveThreadTitle] = useState(DEFAULT_TITLE);
    // The university this conversation is filed under (#1617), or null for a
    // general one. Drives the scope band; never restricts what Astra answers.
    const [activeThreadScope, setActiveThreadScope] = useState(null);
    const [threadMenuOpen, setThreadMenuOpen] = useState(false);
    const [switchingThread, setSwitchingThread] = useState(false);
    const [renamingId, setRenamingId] = useState(null);
    const [renameDraft, setRenameDraft] = useState('');
    const [confirmDeleteId, setConfirmDeleteId] = useState(null);
    // The landing conversation brought in at sign-in (#2945): the open
    // thread's carried part ({count, at}: the divider goes after `count`
    // messages), and the notice across the top with its Start fresh.
    const [carried, setCarried] = useState(null);
    const [carryNotice, setCarryNotice] = useState(() => readNotice());
    const [carryBusy, setCarryBusy] = useState(false);
    const [carryFailed, setCarryFailed] = useState(false);

    const inputRef = useRef(null);
    const scrollRef = useRef(null);
    const panelRef = useRef(null);
    const renameInputRef = useRef(null);
    const bootstrappedRef = useRef(false);
    const historyRef = useRef([]);       // Gemini-shaped {role, parts:[{text}]}
    const activeThreadIdRef = useRef(null);   // mirrors state, read by runTurn without a stale closure
    const activeThreadScopeRef = useRef(null);   // same, for the scoped-ask comparison

    // Held in refs, not read as deps: the page hands these in fresh on every
    // render, and putting them in a useCallback's dep list would rebuild
    // setActiveThread (and everything downstream of it) on each one.
    const onThreadsChangedRef = useRef(onThreadsChanged);
    const onActiveThreadChangedRef = useRef(onActiveThreadChanged);
    onThreadsChangedRef.current = onThreadsChanged;
    onActiveThreadChangedRef.current = onActiveThreadChanged;

    // The list is this component's state; the rail is shown a copy. One
    // owner, one read — the rail never fetches threads itself, or the two
    // would disagree the moment a turn auto-titles a conversation.
    const publishThreads = useCallback((list) => {
        setThreads(list);
        onThreadsChangedRef.current?.(list);
    }, []);

    // `scope` is read from a server payload ({university_id, university_name}
    // on any thread response), so a thread's filing always comes from the
    // stored doc rather than from whatever the client last guessed.
    const setActiveThread = useCallback((id, title, scope = null) => {
        activeThreadIdRef.current = id;
        if (id) setBlankOpen(false);
        // The filing: a school scope (#1617) and/or a topic (#2233). The
        // school chip (AstraPage) and the drawer band below key on
        // university_id, never on this object's truthiness (review
        // 2026-09-16: a truthiness check drew "saved under this school" on
        // every topic-only thread).
        const next = (scope?.university_id || scope?.topic)
            ? { university_id: scope.university_id || null, university_name: scope.university_name || null,
                topic: scope.topic || null }
            : null;
        activeThreadScopeRef.current = next;
        setActiveThreadScope(next);
        setActiveThreadTitle(title || DEFAULT_TITLE);
        onActiveThreadChangedRef.current?.({ id, title: title || DEFAULT_TITLE, scope: next });
    }, []);

    useEffect(() => {
        if (visible) inputRef.current?.focus();
    }, [visible]);

    // The input is disabled while a turn is in flight, and disabling a
    // focused element drops focus to <body> — it never returns on its own
    // (founder 2026-08-02: "every response takes away the focus from the
    // text box"). Hand focus back the moment the turn completes, so the
    // student just keeps typing.
    const prevSendingRef = useRef(false);
    useEffect(() => {
        if (prevSendingRef.current && !sending && visible && !limited) {
            inputRef.current?.focus();
        }
        prevSendingRef.current = sending;
    }, [sending, visible, limited]);
    useEffect(() => {
        if (!scrollRef.current) return;
        // A lead-in alone (the move a door opened her on) is read from its
        // top: scrolled to the bottom, a phone showed the card from its
        // middle, its school and step hidden under the header (#3905).
        const leadInAlone = Boolean(leadIn) && messages.length === 0 && !sending;
        scrollRef.current.scrollTop = leadInAlone ? 0 : scrollRef.current.scrollHeight;
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [messages, sending]);

    // Lightweight Tab-cycle focus trap while the panel is visible — the
    // panel is non-modal (aria-modal="false", the page behind stays
    // reachable by other means) but on mobile it covers the full viewport,
    // so without this a keyboard user tabbing past the last control lands on
    // invisible, covered page content with no focus ring.
    useEffect(() => {
        // A page is not a layer over anything, so trapping Tab inside it
        // would strand a keyboard user in the conversation with no way out to
        // the rail or the nav.
        if (!visible || isPage) return;
        const handleTab = (e) => {
            if (e.key !== 'Tab' || !panelRef.current) return;
            // The effect only attaches while `visible` is true, so every
            // matched control is genuinely on-screen — no extra layout
            // check needed (and none reliable across test environments).
            const focusable = Array.from(panelRef.current.querySelectorAll(FOCUSABLE_SELECTOR))
                .filter((el) => !el.disabled);
            if (!focusable.length) return;
            const first = focusable[0];
            const last = focusable[focusable.length - 1];
            if (e.shiftKey && document.activeElement === first) {
                e.preventDefault();
                last.focus();
            } else if (!e.shiftKey && document.activeElement === last) {
                e.preventDefault();
                first.focus();
            }
        };
        document.addEventListener('keydown', handleTab);
        return () => document.removeEventListener('keydown', handleTab);
    }, [visible, isPage]);

    const refreshThreadsRef = useRef(null);
    const refreshThreads = useCallback(async () => {
        try {
            const res = await listConciergeThreads(email);
            if (res?.success) publishThreads(res.threads || []);
        } catch { /* the switcher just shows what it already had */ }
    }, [email, publishThreads]);
    refreshThreadsRef.current = refreshThreads;

    // Plan, read, answer with streaming (#2555):
    // the reply arrives a sentence at a time, each already past the server's
    // honesty guards, and the finished message replaces the live bubble.
    const [streamText, setStreamText] = useState('');
    const runTurn = useCallback(async (text, { isKickoff = false, threadId = null,
                                               selectedSchoolIds = null,
                                               uploaded = null, from = null } = {}) => {
        setSending(true);
        setError(null);
        // The ready-milestone card is a one-time ANNOUNCEMENT, not durable
        // furniture (founder 2026-08-03: "the great start to profile
        // doesn't disappear even after I've progressed beyond it"). The
        // server only sends `celebrate` once, so clearing it as the next
        // turn starts retires it exactly when it stops being the news.
        setCelebrate(null); setCelebrateCta(null); setCelebrateChoices(null);
        // Recorded immediately, before the network call resolves — a failed
        // turn must not silently drop what the student actually said from
        // the conversation Gemini sees next, even though the visible
        // transcript already shows it.
        if (!isKickoff) {
            historyRef.current = [...historyRef.current, { role: 'user', parts: [{ text }] }];
        }
        const wasFirstTurn = historyRef.current.length <= 1;
        try {
            const turnOpts = {
                kickoff: isKickoff, threadId: threadId ?? activeThreadIdRef.current,
                // Only ever present on the picker's add turn (#1197) — every
                // other turn's request shape is untouched.
                ...(selectedSchoolIds?.length ? { selectedSchoolIds } : {}),
                ...(uploaded?.length ? { uploaded } : {}),
                // a shelf chip's turn (#5258): a label the server counts
                ...(from === 'popular' ? { from } : {}),
            };
            // Only an ordinary question streams: a kickoff, a picker add or
            // an upload acknowledgment keeps the one-shot request.
            const canStream = !isKickoff && !selectedSchoolIds?.length && !uploaded?.length;
            setStreamText('');
            const res = canStream
                ? await conciergeChatStream(email, text, historyRef.current, turnOpts,
                    (piece, meta) => setStreamText((t) => (meta?.reset ? '' : t + piece)))
                : await conciergeChat(email, text, historyRef.current, turnOpts);
            if (res.thread_id) setActiveThread(res.thread_id, res.thread_title || activeThreadTitle, res);
            if (!res.success) {
                setLimited(!!res.limited);
                setError(res.reply || res.error || "Something went wrong — try again.");
                return;
            }
            if (isKickoff && res.history) {
                // The safety-net resume path handed back an existing
                // thread's transcript rather than generating — adopt it
                // verbatim.
                historyRef.current = res.history;
                setMessages([
                    ...(res.restored || []).map((m) => ({ role: m.role, text: m.text })),
                    { role: 'concierge', text: res.reply, savedFields: res.profile_updates_applied || [] },
                ]);
            } else {
                // Only the natural-language reply goes back into history —
                // the full response envelope (success/ready_for_next_step/
                // etc.) is API metadata, not something Gemini should see.
                historyRef.current = [
                    ...historyRef.current,
                    { role: 'model', parts: [{ text: res.reply }] },
                ];
                setMessages((m) => [...m, {
                    role: 'concierge', text: res.reply,
                    savedFields: res.profile_updates_applied || [],
                    activity: res.activity || [],
                    // Pickers belong to the TURN that produced them, so they
                    // ride the message and scroll with it (#1195). Pinned
                    // above the composer they stole permanent height —
                    // founder: "I am unable to read its response as the
                    // options are hiding it" — and outlived their turn —
                    // "when I type the next question, the major chooser is
                    // still there".
                    majorPicker: res.major_picker || null,
                    majorChoicePicker: res.major_choice_picker || null,
                    schoolPicker: res.school_picker || null,
                    // The current list, rendered (#1198) — the answer to a
                    // balance question is rows and counts the server derived
                    // from its own read, not a narration of them.
                    listPanel: res.list_panel || null,
                    essayPicker: res.essay_picker || null,
                    // The screen this turn's reads were about (#1268). The
                    // SERVER derives it from the tools that actually ran —
                    // the model never authors a URL, which is why the prompt
                    // still forbids it from writing links.
                    view: (res.view?.href && res.view?.label)
                        ? { href: res.view.href, label: res.view.label }
                        : null,
                }]);
            }
            // One card at a time: each turn's response replaces (or clears)
            // the pending-action card — the server's TTL owns real expiry.
            setPendingAction(res.pending_action || null);
            setSuggestions(res.suggestions || []);
            // Same one-per-turn rule as the card: the server sends `flow` on
            // every turn a flow is active and null on the turn it ends, so
            // the pill retires itself the instant the outcome lands.
            setFlow(res.flow || null);
            if ((res.activity || []).some((a) => BUILDER_WRITE_TOOLS.includes(a))) {
                // `activity` names the tools that RAN this turn; a held one
                // carries "(needs your confirmation)" and refreshes on confirm.
                notifyProgress();
            }
            if (res.task_notes_applied?.length) {
                // The chat just appended notes onto roadmap rows the student
                // may be looking at — refresh them live (RoadmapPage
                // subscribes to the same bus).
                notifyProgress();
            }
            if (res.profile_updates_applied?.length) {
                onProfileMaybeChanged?.();
                // Two buses, deliberately. This CustomEvent carries WHICH
                // fields changed and has one listener, the Profile page,
                // which highlights them.
                window.dispatchEvent(new CustomEvent('stratia:profile-updated', {
                    detail: { fields: res.profile_updates_applied },
                }));
                // ...and the plain app-wide signal, for every surface that
                // just needs to re-read. An interview turn capturing a GPA
                // or an intended major is a real write, and the comment
                // here used to claim the Getting-Started card refreshed
                // from the event above — it does not, it listens on this
                // one. So that card, MajorMap, Launchpad and the activity
                // ideas all stayed stale through the whole interview.
                notifyProgress();
            }
            // Take the student to the page the answer came from (founder
            // 2026-08-05). Client-side, so the panel and this thread survive
            // — a full navigation would destroy the conversation. Only when
            // the server found ONE unambiguous destination, and never when
            // they are already looking at it.
            if (!onAstraPage && res.view?.href && res.view.navigate !== false && !isHere(res.view.href)) {
                try { navigate(res.view.href); } catch { /* routing is a bonus */ }
            }
            if (res.ready_for_next_step) setReady(true);
            if (res.celebrate) {
                setCelebrate(res.celebrate);
                setCelebrateCta(res.celebrate_cta || null);
                setCelebrateChoices(res.celebrate_choices?.length ? res.celebrate_choices : null);
            }
            // A brand-new thread's title only exists after the first real
            // turn (server auto-titles it) — refresh the switcher list so
            // it shows up named instead of stuck on "New conversation".
            if (wasFirstTurn || isKickoff) refreshThreads();
        } catch {
            setError("I'm having trouble connecting — try again in a moment.");
        } finally {
            setSending(false);
            setStreamText('');
        }
    }, [email, onProfileMaybeChanged, setActiveThread, activeThreadTitle, refreshThreads, onAstraPage]);

    const resetPanelState = () => {
        setReady(false); setCelebrate(null); setCelebrateCta(null); setCelebrateChoices(null);
        setSuggestions([]); setError(null); setLimited(false);
        setPendingAction(null); setActionBusy(false); setFlow(null);
    };

    // The confirmation contract (ADR 0004): only this click — never the
    // model — executes a proposed action. The nonce is all we send; the
    // tool + args are frozen server-side.
    const resolveAction = async (approve) => {
        if (!pendingAction || actionBusy) return;
        const { nonce, title } = pendingAction;
        const confirmedAt = Date.now();
        setActionBusy(true);
        // The ADR-0004 card is the product's trust gate, and its confirm rate
        // was unmeasured in PostHog (audit H3). The tool name is a
        // server-authored identifier — enum-safe by construction.
        track(approve ? EVENTS.CONCIERGE_ACTION_CONFIRMED : EVENTS.CONCIERGE_ACTION_DECLINED,
              { action_type: pendingAction.tool || 'unknown' });
        try {
            const res = approve
                ? await confirmConciergeAction(email, nonce, activeThreadIdRef.current)
                : await declineConciergeAction(email, nonce);
            setPendingAction(null);
            if (approve) {
                const line = res?.success
                    ? (res.summary || 'Done.')
                    : (res?.message || res?.error || "That didn't go through — try asking again.");
                // An add returns once the schools are listed; their analysis
                // runs after, and the batch rides back for the status line (#2938, #2942).
                const addProgress = (res?.success && res.progress?.batch_id) ? res.progress : null;
                if (addProgress) {
                    track(EVENTS.ASTRA_ADD_LISTED, {
                        schools: addProgress.schools?.length || 0, ms: Date.now() - confirmedAt,
                    });
                }
                setMessages((m) => [...m, {
                    addProgress, confirmedAt,
                    role: 'concierge', text: line,
                    activity: res?.success ? [`✓ ${title}`] : [],
                    // The next step the completed action hands over (#1195),
                    // attached to the confirmation message like any other
                    // turn-scoped affordance.
                    majorPicker: (res?.success && res.major_picker) || null,
                    // Two slots filled from the school's OWN ranked majors
                    // (founder 2026-08-05). Same turn-scoped rule as the
                    // other pickers: only the newest message shows it.
                    majorChoicePicker: (res?.success && res.major_choice_picker) || null,
                    // The way in to whatever just got created (#1189). The
                    // SERVER derives it from the tool's own result — the
                    // model never authors a URL, and a message that carries
                    // none simply shows no link rather than guessing one.
                    // No success gate: a billing refusal carries its way
                    // out (upgrade/top-up on the pricing page) — a failed
                    // card with a dead-end message was the founder's exact
                    // screenshot. The server only ever attaches links it
                    // derived itself.
                    view: (res?.view?.href && res.view?.label)
                        ? { href: res.view.href, label: res.view.label }
                        : null,
                }]);
                historyRef.current = [
                    ...historyRef.current,
                    { role: 'model', parts: [{ text: line }] },
                ];
                // A completed action hands over its next step (#1195,
                // founder: "shouldn't it proceed to the next step
                // automatically?"). The server derives it from the tool's
                // own result, so the picker appears the moment the map
                // exists — no extra turn, nothing to type.
                if (res?.success) {
                    onProfileMaybeChanged?.();
                    // The chat writes to the SAME data the page behind it is
                    // already showing, so that page is now stale — the
                    // founder was hard-refreshing after every save. Broadcast
                    // on the app-wide bus the checklist already uses; each
                    // page re-reads itself. Fire-and-forget by design: a page
                    // that isn't mounted has nothing to refresh, and the
                    // chat must not depend on who is listening.
                    notifyProgress();
                }
            }
        } catch {
            // Honest about uncertainty (ADK code-review learning, 2026-08-03):
            // a network failure AFTER the request left means we don't know
            // whether it ran — claiming "NOT run" could be false. Every
            // confirmable backend action is idempotent/completing server-side
            // (#342), so re-asking is always safe.
            setError("Couldn't confirm whether that went through — check the page, or just ask me again (retrying is safe).");
        } finally {
            setActionBusy(false);
        }
    };

    // The conversation on screen is hidden the moment another one is asked
    // for, never when it arrives (#3151, filmed 2026-10-04: a new conversation
    // on /astra showed the previous thread until the new one loaded). Hidden,
    // not dropped: the history the next turn sends stays the open thread's
    // until the switch lands, and a failed switch puts the transcript back.
    // The server saves a thread's history from what the client sends, so an
    // emptied history after a failed switch would overwrite the whole saved
    // conversation on the next message (review of #3151).
    const transcriptSnapRef = useRef(null);
    const hideTranscript = () => {
        const snap = {};
        transcriptSnapRef.current = snap;
        setMessages((prev) => { snap.messages = prev; return []; });
        setCarried((prev) => { snap.carried = prev; return null; });
        setActiveThreadTitle((prev) => { snap.title = prev; return DEFAULT_TITLE; });
    };
    const restoreTranscript = () => {
        const snap = transcriptSnapRef.current;
        transcriptSnapRef.current = null;
        if (!snap) return;
        if (snap.messages) setMessages(snap.messages);
        if ('carried' in snap) setCarried(snap.carried);
        if (snap.title) setActiveThreadTitle(snap.title);
    };

    const openThread = useCallback(async (threadId) => {
        setThreadMenuOpen(false);
        setSwitchingThread(true);
        resetPanelState();
        if (threadId !== activeThreadIdRef.current) hideTranscript();
        try {
            const res = await getConciergeThreadMessages(email, threadId);
            if (res?.success && res.messages?.length > 0) {
                setActiveThread(threadId, res.title, res);
                setMessages(toDisplayMessages(res.messages));
                historyRef.current = toGeminiHistory(res.messages);
                // `read` (#3480): the read carried in at sign-in, drawn as its
                // card at its own message (mock B2).
                setCarried(res.carried?.count ? { count: res.carried.count, at: res.carried.at || null,
                    read: res.carried.read && Number.isInteger(res.carried.read.index) && res.carried.read.card?.name
                        ? res.carried.read : null } : null);
                // The redo chips under Astra's fixed line, until she replies.
                if (res.carried?.chips?.length) setSuggestions(res.carried.chips);
            } else {
                setCarried(null);
                // Empty (a freshly created thread, or a load failure) —
                // treat it as new and get the real first greeting.
                setActiveThread(threadId, res?.title, res);
                setMessages([]);
                historyRef.current = [];
                await runTurn(KICKOFF, { isKickoff: true, threadId });
            }
        } catch {
            // Only when the switch never happened: past setActiveThread the
            // screen belongs to the new thread.
            if (activeThreadIdRef.current !== threadId) restoreTranscript();
            setError("Couldn't open that conversation — try again in a moment.");
        } finally {
            transcriptSnapRef.current = null;
            setSwitchingThread(false);
        }
    }, [email, runTurn, setActiveThread]);

    // A blank, unsaved conversation (#3786): nothing on screen, no model
    // turn; the student's first message makes the thread server-side.
    const blankThread = useCallback(() => {
        // Never under a turn in flight: its reply would land alone on the
        // blank screen. The move still shows above the open conversation.
        if (sending) return;
        blankRequestedRef.current = true;
        setThreadMenuOpen(false);
        resetPanelState();
        transcriptSnapRef.current = null;
        setActiveThread(null, null, null);
        setCarried(null);
        setMessages([]);
        historyRef.current = [];
        setBlankOpen(true);
    }, [setActiveThread, sending]);   // eslint-disable-line react-hooks/exhaustive-deps

    const startNewThread = useCallback(async () => {
        setThreadMenuOpen(false);
        setSwitchingThread(true);
        resetPanelState();
        hideTranscript();
        try {
            const res = await createConciergeThread(email);
            if (res?.success) {
                transcriptSnapRef.current = null;
                setActiveThread(res.id, res.title, res);
                setCarried(null);
                setMessages([]);
                historyRef.current = [];
                await runTurn(KICKOFF, { isKickoff: true, threadId: res.id });
                refreshThreads();
            } else {
                restoreTranscript();
                setError("Couldn't start a new conversation — try again in a moment.");
            }
        } catch {
            restoreTranscript();
            setError("Couldn't start a new conversation — try again in a moment.");
        } finally {
            setSwitchingThread(false);
        }
    }, [email, runTurn, refreshThreads, setActiveThread]);

    // Bootstrap exactly once: resume the most recent thread (free — a pure
    // read) if one exists, else this is the student's very first-ever
    // message, so fire a real kickoff and let the server create the thread.
    //
    // UNLESS the panel was opened AT a specific conversation — the Research
    // drawer's "Resume" (#1617). The launcher only mounts this panel on the
    // first open, so a session's first Resume click arrives with bootstrap
    // still to run; both would call openThread and bootstrap's read, starting
    // later, would land last and win. The student would then be reading an
    // unrelated conversation with the wrong school named in the scope band.
    //
    // A LATCH, never cleared. Reading the prop as-of-now is precisely what
    // loses this race: the resume effect calls onThreadConsumed synchronously,
    // the launcher answers with setPendingThread(null), and that re-render
    // clears the flag BEFORE bootstrap's list read resolves — so an as-of-now
    // check passes and bootstrap opens list[0] anyway. What bootstrap needs to
    // know is "was a conversation ever named on this mount", which is a fact
    // that cannot become false.
    const requestedThreadRef = useRef(false);
    if (initialThreadId) requestedThreadRef.current = true;
    // Astra Today (#3786): a door that opens on a move starts on a blank,
    // unsaved conversation with the move written above it, never on the last
    // thread (the founder clicked "1 thing for you" and read an old Michigan
    // thread with the move nowhere on screen). A latch for the same reason as
    // requestedThreadRef. No kickoff turn: the move's own words are the
    // greeting, and the student's first message creates the thread.
    const blankRequestedRef = useRef(false);
    if (startBlank) blankRequestedRef.current = true;
    useEffect(() => {
        if (bootstrappedRef.current) return;
        bootstrappedRef.current = true;
        (async () => {
            // A landing conversation still in this tab is saved first (#2945),
            // so the list below already holds it and nothing kicks off a
            // greeting beside it. Resolves at once when there is none.
            const fresh = await runCarryImportWithin(email);
            try {
                const res = await listConciergeThreads(email);
                const list = res?.success ? (res.threads || []) : [];
                publishThreads(list);
                // The resume effect owns the opening in that case; loading the
                // switcher's list above is still useful, so it stays.
                if (requestedThreadRef.current) return;
                if (blankRequestedRef.current) { setBlankOpen(true); return; }
                // Just brought over: it opens first, once per page life.
                if (fresh && !fresh.archived && claimCarryOpen(fresh.thread_id)) {
                    await openThread(fresh.thread_id);
                    return;
                }
                if (list.length > 0) {
                    await openThread(list[0].id);
                    return;
                }
            } catch { /* fall through to a fresh kickoff */ }
            if (requestedThreadRef.current) return;
            if (blankRequestedRef.current) { setBlankOpen(true); return; }
            await runTurn(KICKOFF, { isKickoff: true, threadId: null });
        })();
    }, [email]);   // eslint-disable-line react-hooks/exhaustive-deps

    // The NEW title is an argument, not a read of this component's rename
    // draft: the drawer's inline editor owns that draft, and the page's rail
    // owns its own. One rename implementation, two editors — the alternative
    // was a second call to renameConciergeThread in the rail, which is how a
    // surface quietly stops refreshing the list after a rename.
    const renameThread = async (threadId, nextTitle) => {
        const title = (nextTitle ?? '').trim();
        setRenamingId(null);
        if (!title) return;
        const res = await renameConciergeThread(email, threadId, title);
        if (res?.success) {
            if (threadId === activeThreadIdRef.current) setActiveThreadTitle(title);
            refreshThreads();
        }
    };

    const commitRename = (threadId) => renameThread(threadId, renameDraft);

    const confirmDelete = async (threadId) => {
        setConfirmDeleteId(null);
        const res = await deleteConciergeThread(email, threadId);
        if (res?.success) {
            if (readNotice()?.thread_id === threadId) writeNotice(null);
            if (threadId === activeThreadIdRef.current) {
                // Deleted the open conversation — fall back to whatever's
                // next, or start fresh if that was the only one.
                const remaining = threads.filter((t) => t.id !== threadId);
                if (remaining.length > 0) await openThread(remaining[0].id);
                else await startNewThread();
            }
            refreshThreads();
        }
    };

    // The rail's hands (#2210). Re-assigned on every render rather than
    // memoized, so a handler the page calls is never a stale closure over an
    // old thread list — confirmDelete in particular reads `threads` to decide
    // what to open next.
    useEffect(() => {
        if (!chatApi) return undefined;
        chatApi.current = {
            startNewThread, blankThread, openThread, renameThread, confirmDelete, refreshThreads,
        };
        return () => { chatApi.current = null; };
    });

    // The notice follows the tab's copy wherever it changes (the launcher's
    // import can land while this panel is already open).
    useEffect(() => {
        // A late import (past the bootstrap's wait) shows up in the rail too.
        const sync = () => { setCarryNotice(readNotice()); refreshThreadsRef.current?.(); };
        window.addEventListener(CARRY_NOTICE_EVENT, sync);
        return () => window.removeEventListener(CARRY_NOTICE_EVENT, sync);
    }, []);

    // Start fresh: the carried thread is archived (kept, out of the rail) and
    // a new conversation opens if it was the one on screen. Undo restores it.
    const carryStartFresh = async () => {
        const notice = readNotice();
        if (!notice || carryBusy) return;
        setCarryBusy(true);
        setCarryFailed(false);
        const ok = await setCarryArchived(email, notice, true);
        if (ok) {
            if (activeThreadIdRef.current === notice.thread_id) await startNewThread();
            else refreshThreads();
        } else {
            setCarryFailed(true);
        }
        setCarryBusy(false);
    };
    const carryUndo = async () => {
        const notice = readNotice();
        if (!notice || carryBusy) return;
        setCarryBusy(true);
        setCarryFailed(false);
        const ok = await setCarryArchived(email, notice, false);
        if (ok) {
            await openThread(notice.thread_id);
            refreshThreads();
        } else {
            setCarryFailed(true);
        }
        setCarryBusy(false);
    };

    const send = () => {
        track(EVENTS.CONCIERGE_MESSAGE_SENT, { suggestion: false });
        const text = draft.trim();
        if (!text || sending || limited) return;
        setMessages((m) => [...m, { role: 'student', text }]);
        setDraft('');
        runTurn(text);
    };

    // Document upload in chat (founder 2026-08-06): the file rides the SAME
    // profile-builder pipeline (/upload-profile — auth, GCS, extraction,
    // profile merge); Astra is then told it happened via the `uploaded`
    // payload field and acknowledges what was actually extracted. Uploading
    // IS the consent, exactly as on the Profile page.
    const fileInputRef = useRef(null);
    const [uploadingFile, setUploadingFile] = useState(false);
    const handleFilePicked = async (e) => {
        const file = e.target.files?.[0];
        e.target.value = '';   // same file re-pickable
        if (!file || sending || limited || uploadingFile) return;
        setUploadingFile(true);
        setError(null);
        setMessages((m) => [...m, { role: 'student', text: `📎 Shared ${file.name}` }]);
        try {
            const up = await uploadStudentProfile(file, email);
            if (!up?.success) {
                setError(up?.error || "That file couldn't be read — try a PDF, DOCX, TXT or a photo.");
                return;
            }
            onProfileMaybeChanged?.();
            // The extraction just merged real fields — refresh every surface
            // that reads the profile, and LAND the student on the profile
            // page so they watch it fill in (founder 2026-08-06: "as soon as
            // the document is uploaded, we should fetch the profile once
            // more and navigate to the profile page").
            window.dispatchEvent(new CustomEvent('stratia:profile-updated', {
                detail: { fields: ['document_upload'] },
            }));
            notifyProgress();
            if (!onAstraPage && !isHere('/profile')) navigate('/profile');
            await runTurn(`(shared a document: ${file.name})`,
                          { uploaded: [file.name] });
        } catch (err) {
            // uploadStudentProfile rethrows the server's own human message
            // ("…the per-file limit is 10 MB", "Unsupported file type…") —
            // a bare catch turned those unretryable rejections into "try
            // again" (review blocker). Same surfacing as Profile.jsx.
            setError(err?.message || "That file couldn't be uploaded — try again.");
        } finally {
            setUploadingFile(false);
        }
    };

    // A suggestion with no href sends its own label as the student's next
    // turn — the agentic loop takes it from there (e.g. "Map majors to my
    // strengths" reads the profile and proposes generate_major_map with a
    // real confirmation card, instead of navigating to a page that then
    // discovers the same prerequisite itself — founder 2026-08-03).
    // `opts` carries the picker's own ids on the add path (#1197) — the
    // student's visible message stays a plain sentence; the ids travel as
    // data so nothing has to re-derive them.
    const sendSuggestion = (label, opts) => {
        track(EVENTS.CONCIERGE_MESSAGE_SENT, { suggestion: true, ...(opts?.from === 'popular' ? { from: 'popular' } : {}) });
        setMessages((m) => [...m, { role: 'student', text: label }]);
        runTurn(label, opts);
    };

    // A scoped ask (#1617) opens its OWN thread filed under that school,
    // rather than dropping a San-Diego-State question into whatever
    // conversation happened to be open — that mis-filing is exactly what the
    // card's Research drawer exists to prevent. Already in a thread for the
    // same school? Then just keep talking in it.
    //
    // The question is sent as a normal turn, not a kickoff, so the server's
    // deterministic auto-title names the thread after what was actually asked
    // ("Is CS direct-admit at SDSU?") instead of "New conversation".
    const startScopedThread = useCallback(async (scope, question) => {
        setSwitchingThread(true);
        try {
            // Already have a conversation for this school? Continue it. The
            // point of filing is that a school's history lives in ONE place —
            // bouncing between two cards (SDSU → Duke → SDSU) must not leave a
            // trail of one-question threads for the drawer to list.
            const existing = threads.find((t) => t.university_id === scope.university_id);
            if (existing) {
                await openThread(existing.id);
                sendSuggestion(question);
                return;
            }
            const res = await createConciergeThread(email, scope);
            if (!res?.success) {
                // Fall back to asking in the current thread: a filing problem
                // must never cost the student their question.
                sendSuggestion(question);
                return;
            }
            resetPanelState();
            setActiveThread(res.id, res.title, res);
            // A new thread carried nothing in (#4514): the previous thread's
            // "Before you signed in" divider sat over a question asked signed in.
            setCarried(null);
            setMessages([{ role: 'student', text: question }]);
            historyRef.current = [];
            // The new thread is on screen now: nothing is loading while the
            // turn runs, and the typing dots say Astra is answering (#3762:
            // "Loading conversation..." sat over the question for the whole
            // answer).
            setSwitchingThread(false);
            await runTurn(question, { threadId: res.id });
            refreshThreads();
        } finally {
            setSwitchingThread(false);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [email, threads, openThread, runTurn, refreshThreads, setActiveThread]);

    // A question filed under a TOPIC (#2233, founder 2026-09-15: a question
    // from the Activities page landed in the "College List" thread because
    // that was the one open). The most relevant thread is the latest one
    // with the same topic and no school; failing that, a new one titled for
    // the topic. Same shape as startScopedThread, same fallback: a filing
    // problem never costs the student their question.
    const startTopicThread = useCallback(async (topic, question) => {
        setSwitchingThread(true);
        try {
            const existing = threads.find((t) => t.topic === topic && !t.university_id);
            if (existing) {
                await openThread(existing.id);
                sendSuggestion(question);
                return;
            }
            const res = await createConciergeThread(email, { topic });
            if (!res?.success) {
                sendSuggestion(question);
                return;
            }
            resetPanelState();
            setActiveThread(res.id, res.title, res);
            // A new thread carried nothing in (#4514): the previous thread's
            // "Before you signed in" divider sat over a question asked signed in.
            setCarried(null);
            setMessages([{ role: 'student', text: question }]);
            historyRef.current = [];
            // The new thread is on screen now: nothing is loading while the
            // turn runs, and the typing dots say Astra is answering (#3762:
            // "Loading conversation..." sat over the question for the whole
            // answer).
            setSwitchingThread(false);
            await runTurn(question, { threadId: res.id });
            refreshThreads();
        } finally {
            setSwitchingThread(false);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [email, threads, openThread, runTurn, refreshThreads, setActiveThread]);

    // A question handed in from another surface (Major Map's "Talk it through
    // with Astra"). It waits for `ready` — firing before the session exists
    // would drop the turn — and the ref makes it fire EXACTLY once: without it
    // the effect re-runs on every render while `initialAsk` is still set, and
    // the student watches their question be asked over and over.
    const askedRef = useRef(null);
    useEffect(() => {
        // NOT `ready` — that flag means "hit the profile milestone", so gating on
        // it would hold the question until the student finished onboarding, or
        // forever. The real precondition is that the conversation exists: the
        // kickoff greeting has landed and no turn is in flight.
        if (!visible || !initialAsk || sending || (messages.length === 0 && !blankOpen)) return;
        if (askedRef.current === initialAsk) return;
        askedRef.current = initialAsk;
        const wantScope = initialScope?.university_id || null;
        if (wantScope && wantScope !== activeThreadScopeRef.current?.university_id) {
            startScopedThread(initialScope, initialAsk);
        } else if (!wantScope && initialTopic && initialTopic !== activeThreadScopeRef.current?.topic) {
            startTopicThread(initialTopic, initialAsk);
        } else {
            sendSuggestion(initialAsk);
        }
        onAskConsumed?.();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [visible, initialAsk, sending, messages.length, blankOpen]);

    // "Resume" on a card's Research drawer names an existing thread — open it
    // directly (a free read), exactly as picking it from the switcher would.
    const resumedRef = useRef(null);
    useEffect(() => {
        // Cleared the moment the launcher takes the request back, so resuming
        // the SAME conversation again later — open it, switch away, click
        // Resume on that card once more — still works. A ref that latched
        // forever would silently do nothing on the second click.
        if (!initialThreadId) { resumedRef.current = null; return; }
        if (!visible || sending) return;
        if (resumedRef.current === initialThreadId) return;
        resumedRef.current = initialThreadId;
        openThread(initialThreadId);
        onThreadConsumed?.();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [visible, initialThreadId, sending]);

    // While the celebrate card is up, the chip row must not repeat any of
    // the card's own choices — matched by the server-assigned action id,
    // never by comparing URL strings.
    const celebrateIds = new Set((celebrateChoices?.length ? celebrateChoices
        : (celebrateCta ? [celebrateCta] : [])).map((c) => c.id));
    // On the Builder thread the chips stand down entirely (founder
    // 2026-09-22: "See my odds at every school" beside interview question
    // one is the competing controller). The server already sends none for
    // a builder-topic thread; this keeps a cached or older reply honest.
    const builderThread = activeThreadScope?.topic === 'builder';
    const visibleSuggestions = builderThread ? []
        : (celebrate && celebrateIds.size ? suggestions.filter((s) => !celebrateIds.has(s.id)) : suggestions);

    // The page's opening moment: nothing said yet, or nothing but her
    // greeting. It is where the mock puts her mark and the three chips, and
    // it ends the instant the student answers.
    const freshThread = messages.length <= 1
        && (messages.length === 0 || messages[0].role === 'concierge');
    const openingScreen = isPage && freshThread;
    // The shelf sits under her own next steps while nothing has been said,
    // on the page's opening screen and in the drawer alike (#5258).
    // Only once the greeting landed (or a blank conversation settled): an
    // empty list while a kept thread loads is not a fresh one (review).
    const shelf = freshThread && (messages.length === 1 || blankOpen) && !sending && !limited && !builderThread
        ? studentShelf(popular) : null;
    const askPopular = (label) => sendSuggestion(label, { from: 'popular' });

    // The newest concierge reply long enough to be an ANSWER (not a
    // greeting or a one-line confirmation) carries the worded ask.
    const lastSubstantiveIdx = (() => {
        for (let j = messages.length - 1; j >= 0; j -= 1) {
            const mm = messages[j];
            if (mm.role === 'concierge' && (mm.text || '').length >= 80) return j;
        }
        return -1;
    })();

        return (
        <div
            ref={panelRef}
            {...(isPage ? {} : {
                role: 'dialog',
                'aria-label': 'Astra',
                'aria-modal': 'false',
                'aria-hidden': !visible,
                onKeyDown: (e) => { if (e.key === 'Escape') onClose?.(); },
            })}
            data-testid="concierge-chat"
            data-layout={layout}
            className={isPage
                ? 'flex h-full min-h-0 w-full flex-col bg-white'
                : `fixed bottom-0 right-0 z-40 flex h-[min(640px,100dvh)] w-full flex-col
                border border-[#E0DED8] bg-white shadow-2xl
                sm:bottom-6 sm:right-6 sm:h-[600px] sm:w-[400px] sm:rounded-2xl
                ${visible ? '' : 'invisible pointer-events-none'}`}
        >
            {!isPage && (
            <header className="relative flex items-center justify-between gap-2 rounded-t-none border-b
                border-[#E0DED8] bg-[#1A4D2E] px-4 py-3.5 sm:rounded-t-2xl">
                <button onClick={() => setThreadMenuOpen((v) => !v)} data-testid="concierge-thread-menu-toggle"
                        aria-expanded={threadMenuOpen} aria-label="Switch or start a conversation"
                        className="flex min-w-0 flex-1 items-center gap-2 rounded-lg py-0.5 pr-1 text-left hover:bg-white/10">
                    <AstraAvatar size={24} />
                    <div className="min-w-0">
                        <p className="flex items-center gap-1.5 text-sm font-semibold text-white">
                            Astra
                            
                        </p>
                        {/* Titles are the student's first message (#4475). */}
                        <p className={`${REPLAY_PRIVATE_CLASS} truncate text-[11px] text-[#C7E3CB]`}>{activeThreadTitle}</p>
                    </div>
                    <ChevronDownIcon className={`h-3.5 w-3.5 shrink-0 text-[#C7E3CB] transition-transform ${threadMenuOpen ? 'rotate-180' : ''}`} />
                </button>
                <button onClick={onClose} aria-label="Close" data-testid="concierge-close"
                        tabIndex={visible ? 0 : -1}
                        className="rounded-lg p-1.5 text-white/80 hover:bg-white/10 hover:text-white">
                    <XMarkIcon className="h-5 w-5" />
                </button>

                {threadMenuOpen && (
                    <div data-testid="concierge-thread-menu"
                         className="absolute left-3 right-3 top-[calc(100%+6px)] z-10 max-h-72 overflow-y-auto
                             rounded-xl border border-[#E0DED8] bg-white p-1.5 shadow-xl">
                        <button onClick={startNewThread} data-testid="concierge-new-thread"
                                className="flex w-full items-center gap-2 rounded-lg px-2.5 py-2 text-left text-sm font-semibold text-[#1A4D2E] hover:bg-[#F0F7F1]">
                            <PlusIcon className="h-4 w-4" /> New conversation
                        </button>
                        {threads.length > 0 && <div className="my-1 border-t border-[#F0EEE8]" />}
                        {threads.map((t) => (
                            <div key={t.id} data-testid={`concierge-thread-${t.id}`}
                                 className={`${REPLAY_PRIVATE_CLASS} group flex items-center gap-1 rounded-lg px-1 ${
                                     t.id === activeThreadIdRef.current ? 'bg-[#F0F7F1]' : 'hover:bg-[#FAFAF7]'}`}>
                                {renamingId === t.id ? (
                                    <>
                                        <input ref={renameInputRef} value={renameDraft}
                                               onChange={(e) => setRenameDraft(e.target.value)}
                                               onKeyDown={(e) => {
                                                   if (e.key === 'Enter') commitRename(t.id);
                                                   if (e.key === 'Escape') setRenamingId(null);
                                               }}
                                               aria-label="Conversation title" autoFocus
                                               className="min-w-0 flex-1 rounded-md border border-[#1A4D2E] px-2 py-1.5 text-sm" />
                                        <button onClick={() => commitRename(t.id)} aria-label="Save name"
                                                className="shrink-0 rounded-md p-1.5 text-[#1A4D2E] hover:bg-[#E5F0E5]">
                                            <CheckIcon className="h-3.5 w-3.5" />
                                        </button>
                                    </>
                                ) : confirmDeleteId === t.id ? (
                                    <div className="flex w-full items-center justify-between gap-2 px-2 py-1.5 text-xs">
                                        <span className="text-[#7A3A22]">Delete this conversation?</span>
                                        <div className="flex gap-1 shrink-0">
                                            <button onClick={() => confirmDelete(t.id)} data-testid={`concierge-confirm-delete-${t.id}`}
                                                    className="rounded-md bg-[#C05838] px-2 py-1 font-semibold text-white">Delete</button>
                                            <button onClick={() => setConfirmDeleteId(null)}
                                                    className="rounded-md px-2 py-1 font-semibold text-[#6B6B6B] hover:bg-[#F0EEE8]">Cancel</button>
                                        </div>
                                    </div>
                                ) : (
                                    <>
                                        <button onClick={() => openThread(t.id)} data-testid={`concierge-open-thread-${t.id}`}
                                                className="flex min-w-0 flex-1 items-center gap-2 rounded-lg px-2 py-2 text-left text-sm text-[#2C2C2C]">
                                            <span className="min-w-0 flex-1 truncate">{t.title}</span>
                                            {/* Which school this one is filed under, so a list
                                                mixing scoped and general conversations is
                                                scannable. Text, not colour — the name IS the
                                                signal. */}
                                            {t.university_name && (
                                                <span data-testid={`concierge-thread-scope-${t.id}`}
                                                      className="shrink-0 max-w-[38%] truncate text-[10px] text-[#8A8A8A]">
                                                    {t.university_name}
                                                </span>
                                            )}
                                        </button>
                                        <button onClick={() => { setRenamingId(t.id); setRenameDraft(t.title); }}
                                                aria-label={`Rename ${t.title}`} data-testid={`concierge-rename-${t.id}`}
                                                className="shrink-0 rounded-md p-1.5 text-[#9A968C] opacity-0 hover:bg-[#F0EEE8] hover:text-[#4A4A4A] group-hover:opacity-100 group-focus-within:opacity-100">
                                            <PencilIcon className="h-3.5 w-3.5" />
                                        </button>
                                        <button onClick={() => setConfirmDeleteId(t.id)}
                                                aria-label={`Delete ${t.title}`} data-testid={`concierge-delete-${t.id}`}
                                                className="shrink-0 rounded-md p-1.5 text-[#9A968C] opacity-0 hover:bg-[#FCEEE8] hover:text-[#C05838] group-hover:opacity-100 group-focus-within:opacity-100">
                                            <TrashIcon className="h-3.5 w-3.5" />
                                        </button>
                                    </>
                                )}
                            </div>
                        ))}
                    </div>
                )}
            </header>
            )}

            {/* SCOPE BAND (#1617) — only for a conversation filed under a
                school. A general thread renders nothing here, so the panel a
                student opens from the nav is unchanged.

                The wording is deliberate and was the founder-facing copy call:
                "Saved under" is the truth (it is where the conversation is
                FILED), whereas anything like "Astra will focus on X" would
                promise a restriction the harness does not enforce — she
                answers whatever is asked next, and the thread still lives
                here. The second clause names real tools that genuinely take a
                university id, so it is not a vague reassurance either. */}
            {!isPage && activeThreadScope?.university_id && (
                <div data-testid="concierge-scope-band"
                     className="flex flex-wrap items-center gap-x-2 gap-y-1 border-b border-[#DCE7DD]
                         bg-[#F0F7F1] px-4 py-2">
                    <span data-testid="concierge-scope-chip"
                          className="inline-flex max-w-full items-center gap-1.5 rounded-full border
                              border-[#CFE0D2] bg-white px-2.5 py-1 text-xs font-semibold text-[#1A4D2E]">
                        <span aria-hidden="true">🎓</span>
                        <span className="truncate">
                            About: {activeThreadScope.university_name || 'this school'}
                        </span>
                    </span>
                    <span className="min-w-0 flex-1 text-[11px] leading-snug text-[#4A6B53]">
                        {SCOPE_BAND_LINE}
                    </span>
                </div>
            )}

            <CarryBanner notice={carryNotice} busy={carryBusy} failed={carryFailed}
                         onStartFresh={carryStartFresh} onUndo={carryUndo} onDismiss={dismissNotice} />

            <div ref={scrollRef}
                 className={isPage
                     ? 'flex-1 space-y-3 overflow-y-auto px-4 py-6 sm:px-6'
                     : 'flex-1 space-y-3 overflow-y-auto px-4 py-4'}
                 aria-live="polite">
                {/* What a door opened her ON (#3789: the move a badge, a pill
                    or a notification promised) is her first message: it
                    scrolls with the conversation, the input stays pinned, and
                    the generic opening screen steps aside. */}
                {leadIn}
                {/* The opening screen of the page (#2210): her mark and one
                    line, until her greeting lands. */}
                {isPage && !leadIn && messages.length === 0 && !switchingThread && !error && (
                    <div data-testid="astra-empty-state"
                         className="flex flex-col items-center gap-3 py-10 text-center">
                        <AstraAvatar size={56} />
                        <p className="font-serif text-xl font-bold text-[#1A2E1F]">
                            Where should we start?
                        </p>
                        <p className="max-w-sm text-sm text-[#6B6B6B]">
                            Astra reads your profile, your list and your roadmap before she answers.
                        </p>
                    </div>
                )}
                {switchingThread && (
                    <p className="text-center text-xs text-[#9A968C]" data-testid="concierge-switching">Loading conversation…</p>
                )}
                {/* The A2 ask (founder pick 2026-08-09) goes on the LATEST
                    substantive reply only — one visible question at a time,
                    never on a short greeting/confirmation, so it can't nag.
                    Older replies keep compact icons via ReplyFeedback. */}
                {messages.map((m, i) => (
                    <React.Fragment key={i}>
                    {carried && i === 0 && <CarryDivider label={carryDividerLabel(carried.at)} testid="carry-divider-before" />}
                    {carried && i === carried.count && <CarryDivider label="Signed in" testid="carry-divider-after" />}
                    {carried?.read && i === carried.read.index && m.role === 'concierge' ? (
                        <ReadCard read={carried.read.card} testid="carried-read" />
                    ) : (
                    <div className={m.role === 'student' ? 'text-right' : ''}>
                        {/* Words, never tool ids (#3154): the wire names
                            the tools that ran; the student reads what
                            Astra did. */}
                        {activityLine(m.activity) && (
                            <p className="mb-1 text-[11px] text-[#9A968C]"
                               data-testid="concierge-activity">
                                {activityLine(m.activity)}
                            </p>
                        )}
                        {/* Blocked in session replay (#4475): the student's
                            words and Astra's reply (which may be the 988
                            answer) never record, text or attributes. */}
                        <span className={`${REPLAY_PRIVATE_CLASS} inline-block max-w-[85%] rounded-2xl px-3.5 py-2.5 text-left text-sm leading-relaxed ${m.role === 'student'
                            ? 'bg-[#1A4D2E] text-white'
                            : 'bg-[#F4F8F4] text-[#2C2C2C]'}`}>
                            {/* Astra writes prose, lists and the occasional
                                table; rendering it as one flat string
                                collapsed even paragraph breaks (founder
                                2026-08-07: "bullet point should appear as
                                bullet point.. right now everything appears
                                as text"). Every other chat in the app already
                                renders markdown — this one was the holdout.
                                The STUDENT's own text stays literal: their
                                message is not a document to interpret. */}
                            {m.role === 'concierge' ? (
                                <div className="astra-md space-y-2 [&_ul]:list-disc [&_ul]:pl-5 [&_ol]:list-decimal [&_ol]:pl-5 [&_li]:my-0.5 [&_strong]:font-semibold [&_a]:underline [&_table]:block [&_table]:overflow-x-auto">
                                    <ReactMarkdown remarkPlugins={[remarkGfm]}>
                                        {m.text}
                                    </ReactMarkdown>
                                </div>
                            ) : m.text}
                        </span>
                        {/* One tap on the ANSWER (founder 2026-08-06). Per
                            reply, not per session — only that tells us which
                            answer landed badly. Greetings are rateable too:
                            a first impression is exactly the kind of answer
                            worth knowing about. */}
                        {m.role === 'concierge' && m.text && (
                            <ReplyFeedback email={email}
                                           threadId={activeThreadIdRef.current}
                                           turnIndex={i} replyText={m.text}
                                           prominent={i === lastSubstantiveIdx} />
                        )}
                        {/* Turn-scoped affordances (#1187/#1188) live INSIDE
                            the transcript, attached to the message that
                            produced them — so the reply stays readable above
                            them and they scroll away with their turn. Only the
                            LATEST message renders them: an older picker is a
                            historical offer, and acting on stale rows (or a
                            map from three turns ago) is not something we want
                            to make possible. */}
                        {i === messages.length - 1 && m.majorPicker && (
                            <div className="mt-2">
                                <MajorPicker picker={m.majorPicker} email={email}
                                             onSend={sendSuggestion} busy={sending} />
                            </div>
                        )}
                        {i === messages.length - 1 && m.majorChoicePicker && (
                            <div className="mt-2">
                                <MajorChoicePicker picker={m.majorChoicePicker} email={email}
                                                   busy={sending}
                                                   onSaved={() => onProfileMaybeChanged?.()}
                                                   key={m.majorChoicePicker.university_id} />
                            </div>
                        )}
                        {i === messages.length - 1 && m.schoolPicker && (
                            <div className="mt-2">
                                <SchoolPicker picker={m.schoolPicker}
                                              onSend={sendSuggestion} busy={sending} />
                            </div>
                        )}
                        {/* Same turn-scoped rule as the pickers (#1198): a list
                            panel is only true for the list as it was when it
                            was read — adding a school changes the bands, so an
                            older panel must not sit above a newer answer. */}
                        {i === messages.length - 1 && m.listPanel && (
                            <div className="mt-2">
                                <ListPanel panel={m.listPanel} />
                            </div>
                        )}
                        {/* The prompts she just named, as rows the student can
                            carry into the Essay Workshop (#2249). Same
                            turn-scoped rule: the panel belongs to the answer
                            that produced it. */}
                        {i === messages.length - 1 && m.essayPicker && (
                            <div className="mt-1">
                                <EssayPromptPicker picker={m.essayPicker} onNavigated={onClose || undefined} />
                            </div>
                        )}
                        {/* The add's analysis, as it really finishes: one line,
                            no bar (#2942, founder pick A; #2938 drew a bar).
                            Not turn-scoped like the pickers: it is a status,
                            not an offer, and it stays true after the next turn. */}
                        {m.addProgress && (
                            <AddStatusLine email={email} progress={m.addProgress}
                                           confirmedAt={m.confirmedAt} />
                        )}
                        {m.view && (
                            /* "Added — its full fit analysis is ready" used to
                               END there (#1189). The link is server-derived
                               from the tool result, so it can only point at
                               something that actually exists. */
                            <div className="mt-1.5">
                                <Link to={m.view.href}
                                      onClick={onClose || undefined}
                                      data-testid="concierge-view-link"
                                      className="inline-flex items-center gap-1 rounded-full border border-[#1A4D2E] px-3 py-1 text-xs font-semibold text-[#1A4D2E] hover:bg-[#F4F8F4]">
                                    {m.view.label} <span aria-hidden="true">→</span>
                                </Link>
                            </div>
                        )}
                        {m.savedFields?.length > 0 && (
                            /* One chip, and it goes somewhere (founder,
                               2026-08-09: "I thought Astra navigated to the
                               profile page... or at least provide a link").
                               A LINK, not auto-navigation — yanking a student
                               out of the conversation mid-interview would
                               punish them for answering. N anonymous "saved"
                               chips said less than one that counts and links. */
                            <div className="mt-1" data-testid="concierge-saved-chip">
                                <Link to="/profile"
                                      className="inline-flex items-center gap-1 rounded-full bg-[#D6E8D5] px-2.5 py-1 text-[11px] font-semibold text-[#1A4D2E] hover:bg-[#C2DCC0] transition-colors">
                                    ✓ Saved to your profile{m.savedFields.length > 1 ? ` (${m.savedFields.length})` : ''}
                                    <span aria-hidden="true">→</span>
                                </Link>
                            </div>
                        )}
                    </div>
                    )}
                    </React.Fragment>
                ))}
                {/* The three chips the mock puts under the greeting. They are
                    the server's OWN next steps — the same ones the composer
                    row shows once the conversation is under way, the first
                    three of them, centred. Nothing is invented here: a turn
                    that proposed no next step shows no chips rather than three
                    questions written on her behalf. */}
                {openingScreen && visibleSuggestions.length > 0 && !sending && !limited && (
                    <div className="flex flex-wrap justify-center gap-1.5 pt-2"
                         data-testid="astra-opening-suggestions">
                        {visibleSuggestions.slice(0, 3).map((sg) => (sg.href ? (
                            <a key={sg.id || sg.label} href={sg.href} onClick={goto(sg.href)}
                               className="rounded-full border border-[#1A4D2E] px-3.5 py-2 text-xs font-semibold text-[#1A4D2E] hover:bg-[#F0F7F1]">
                                {sg.label} →
                            </a>
                        ) : (
                            <button key={sg.id || sg.label} onClick={() => sendSuggestion(sg.label)}
                                    className="rounded-full border border-[#E0DED8] px-3.5 py-2 text-xs font-semibold text-[#4A4A4A] hover:bg-[#F8F6F0]">
                                {sg.label}
                            </button>
                        )))}
                    </div>
                )}
                {isPage && shelf && <PopularShelf shelf={shelf} onAsk={askPopular} busy={sending} />}
                {sending && streamText && (
                    <div data-testid="concierge-streaming">
                        <span className={`${REPLAY_PRIVATE_CLASS} inline-block max-w-[85%] rounded-2xl bg-[#F4F8F4] px-3.5 py-2.5 text-left text-sm leading-relaxed text-[#2C2C2C] whitespace-pre-wrap`}
                              aria-live="polite">
                            {streamText}
                        </span>
                    </div>
                )}
                {sending && !streamText && (
                    <div data-testid="concierge-typing" className="flex items-center gap-1 px-1">
                        {[0, 1, 2].map((i) => (
                            <span key={i} className="h-1.5 w-1.5 animate-bounce rounded-full bg-[#A8C5A6] motion-reduce:animate-none"
                                  style={{ animationDelay: `${i * 120}ms` }} />
                        ))}
                    </div>
                )}
                {celebrate && (
                    <div data-testid="concierge-ready-card"
                         className="relative rounded-2xl border-2 border-[#1A4D2E] bg-[#F0F7F1] p-4 text-center">
                        <button
                            onClick={() => { setCelebrate(null); setCelebrateCta(null); setCelebrateChoices(null); }}
                            data-testid="concierge-dismiss-ready"
                            aria-label="Dismiss"
                            className="absolute right-2 top-2 rounded-lg p-1 text-[#6B8F76] hover:bg-[#E0EFE2] hover:text-[#1A4D2E]">
                            <XMarkIcon className="h-4 w-4" />
                        </button>
                        <p className="font-serif text-base font-bold text-[#1A2E1F]">{celebrate}</p>
                        {/* Every choice is server-chosen data (next-best-action
                            engine) — no route, label, or blurb is hard-coded
                            here. A real choice when there are two (founder
                            2026-08-03: "shouldn't this be a choice?"), one
                            button when there's only one to offer. A choice
                            with no href sends its own label as a turn instead
                            of navigating — see_odds always leads somewhere
                            ready; map_majors routes through the agentic loop
                            so it can explain the tradeoff and propose
                            generate_major_map with a real confirmation card. */}
                        <div className="mt-3 flex flex-col gap-2 text-left">
                            {(celebrateChoices?.length ? celebrateChoices
                                : (celebrateCta ? [celebrateCta] : [])).map((c) => (
                                <div key={c.id || c.label}
                                     className="rounded-xl border border-[#D6E8D5] bg-white px-3.5 py-2.5">
                                    {c.href ? (
                                        <a href={c.href} onClick={goto(c.href)}
                                           data-testid={c.id === 'see_odds' ? 'concierge-see-odds' : `concierge-celebrate-${c.id}`}
                                           className="block text-sm font-semibold text-[#1A4D2E] hover:underline">
                                            {c.label} →
                                        </a>
                                    ) : (
                                        <button onClick={() => sendSuggestion(c.label)}
                                                data-testid={c.id === 'see_odds' ? 'concierge-see-odds' : `concierge-celebrate-${c.id}`}
                                                className="block w-full text-left text-sm font-semibold text-[#1A4D2E] hover:underline">
                                            {c.label} →
                                        </button>
                                    )}
                                    {c.why && <p className="mt-1 text-xs text-[#6B6B6B]">{c.why}</p>}
                                </div>
                            ))}
                        </div>
                    </div>
                )}
                {pendingAction && (
                    <div data-testid="concierge-action-card"
                         className="rounded-2xl border-2 border-[#1A4D2E] bg-[#EAF3E9] p-4">
                        <p className="font-serif text-sm font-bold text-[#1A2E1F]">{pendingAction.title}</p>
                        {pendingAction.subtitle && (
                            <p className="mt-1 text-xs text-[#6B6B6B]">{pendingAction.subtitle}</p>
                        )}
                        <div className="mt-3 flex gap-2">
                            <button onClick={() => resolveAction(true)} disabled={actionBusy}
                                    data-testid="concierge-action-confirm"
                                    className="rounded-xl bg-[#1A4D2E] px-4 py-2 text-sm font-semibold text-white hover:bg-[#2D6B45] disabled:opacity-50">
                                {actionBusy ? 'Working…' : 'Confirm'}
                            </button>
                            <button onClick={() => resolveAction(false)} disabled={actionBusy}
                                    data-testid="concierge-action-decline"
                                    className="rounded-xl border border-[#E0DED8] px-4 py-2 text-sm font-semibold text-[#6B6B6B] hover:bg-[#F8F6F0] disabled:opacity-50">
                                Not now
                            </button>
                        </div>
                    </div>
                )}
            </div>

            {/* Deliberately OUTSIDE the aria-live="polite" region above — an
                alert role already implies aria-live="assertive"; nesting it
                inside a polite region is a known WAI-ARIA anti-pattern that
                causes inconsistent/duplicate announcements across screen
                readers. */}
            {error && (
                <div role="alert" data-testid={limited ? 'concierge-limited' : 'concierge-error'}
                     className="mx-4 mb-2 rounded-xl bg-[#FCEEE8] px-3 py-2 text-sm text-[#7A3A22]">
                    {limited
                        ? "You've reached today's chat limit — come back tomorrow and we'll pick right back up."
                        : error}
                </div>
            )}

            <div className="border-t border-[#E0DED8] p-3">
                {/* "Step 1 of 2" — the flow's own progress, sitting where the
                    student is about to type. Deliberately minimal: the payload
                    carries the flow id but no title, and a humanized id
                    ("Major map intake") is a label nobody wrote, so the pill
                    states only what the server actually knows. */}
                {flow?.total > 0 && (
                    <div className="mb-2 flex" data-testid="concierge-flow-progress">
                        <span className="rounded-full border border-[#D6E8D5] bg-[#F0F7F1] px-2.5 py-1
                            text-[11px] font-semibold text-[#1A4D2E]">
                            Step {flow.step} of {flow.total}
                        </span>
                    </div>
                )}
                {/* Server-attached next-step chips — the harness's "here's
                    what to do next", always one tap away once the profile
                    crosses the ready bar. When the celebrate card is up, its
                    CTA already IS "see my odds" — drop that duplicate from
                    the chips (founder 2026-08-02: "shows me the odds and
                    presents the same question"). */}
                {/* The page shows the first three under the greeting instead,
                    so the composer row stands down until the student has said
                    something. */}
                {visibleSuggestions.length > 0 && !sending && !limited && !openingScreen && (
                    <div className="mb-2 flex flex-wrap gap-1.5" data-testid="concierge-suggestions">
                        {visibleSuggestions.map((s) => (s.href ? (
                            <a key={s.id || s.label} href={s.href} onClick={goto(s.href)}
                               className="rounded-full border border-[#1A4D2E] px-3 py-1.5 text-xs font-semibold text-[#1A4D2E] hover:bg-[#F0F7F1]">
                                {s.label} →
                            </a>
                        ) : (
                            <button key={s.id || s.label} onClick={() => sendSuggestion(s.label)}
                                    className="rounded-full border border-[#E0DED8] px-3 py-1.5 text-xs font-semibold text-[#4A4A4A] hover:bg-[#F8F6F0]">
                                {s.label}
                            </button>
                        )))}
                    </div>
                )}
                {!isPage && shelf && (
                    <div className="mb-2"><PopularShelf shelf={shelf} onAsk={askPopular} busy={sending} /></div>
                )}
                {/* NOTE: the old hard-coded "I have enough — show me my odds"
                    button lived here. It predated the next-best-action engine,
                    from when the celebrate card was the only route to odds —
                    so once `see_odds` became a standing suggestion chip it was
                    a second, redundant door to the same page with hard-coded
                    copy (founder 2026-08-03: "why is it showing two options to
                    view the odds?"). The server-chosen chip is the one door. */}
                <div className="flex items-end gap-2">
                    <input ref={fileInputRef} type="file" className="hidden"
                           accept=".pdf,.docx,.doc,.txt,.md,.jpg,.jpeg,.png,.webp,.heic,.heif"
                           onChange={handleFilePicked} data-testid="concierge-file-input" />
                    <button type="button" onClick={() => fileInputRef.current?.click()}
                            disabled={sending || limited || uploadingFile}
                            title="Share a transcript or resume — Astra reads it into your profile"
                            aria-label="Upload a document"
                            data-testid="concierge-upload"
                            className="rounded-xl border border-[#E0DED8] p-2.5 text-[#6B6B6B]
                                hover:border-[#1A4D2E] hover:text-[#1A4D2E] disabled:opacity-40">
                        <PaperClipIcon className={`h-5 w-5 ${uploadingFile ? 'animate-pulse' : ''}`} />
                    </button>
                    <label htmlFor="concierge-input" className="sr-only">Message the Concierge</label>
                    <textarea
                        id="concierge-input" ref={inputRef} rows={1}
                        value={draft} onChange={(e) => setDraft(e.target.value)}
                        onKeyDown={(e) => { if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); send(); } }}
                        placeholder={limited ? "Come back tomorrow to keep going…"
                            : (isPage ? "Message Astra" : "Type your answer…")}
                        disabled={sending || limited}
                        data-testid="concierge-input"
                        className="flex-1 resize-none rounded-xl border border-[#E0DED8] px-3 py-2.5 text-sm
                            focus:border-[#1A4D2E] focus:outline-none focus:ring-1 focus:ring-[#1A4D2E] disabled:opacity-60"
                    />
                    <button onClick={send} disabled={sending || limited || !draft.trim()} data-testid="concierge-send"
                            className="rounded-xl bg-[#1A4D2E] px-4 py-2.5 text-sm font-semibold text-white
                                hover:bg-[#2D6B45] disabled:opacity-40">
                        Send
                    </button>
                </div>
                {/* "Not right now" dismisses the DRAWER for the session. A
                    page the student navigated to has nothing to dismiss —
                    leaving is the back button. */}
                {!isPage && (
                    <button onClick={onDismissForSession} data-testid="concierge-not-now"
                            className="mt-2 text-[11px] font-medium text-[#6B6B6B] hover:text-[#2C2C2C]">
                        Not right now
                    </button>
                )}
            </div>
        </div>
    );
}
